import { Middleware, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import deepEqual from 'fast-deep-equal';

import { staticService } from '@static-services';
import { aiService } from './aiService.ts';
import { api } from './api';
import { ariService } from './ariService.ts';
import { dataminrService } from './dataminrService.ts';
import { service } from './service';
import { service as adminService } from './serviceAdmin.ts';
import sliceReducer, { sliceSelectors } from './slice';
import adminReducer from './sliceAdmin.ts';
import filtersReducer from './sliceFilters.ts';
import statsReducer from './Views/Stats/slice';
import { wifiService } from './wifiService.ts';

// clear the view from the URL bar when the slice changes
const ignoreActions = ['slice/restoreSnapshot', 'slice/fitBoundsComplete', 'slice/setLocationNetworkPolygonId'];
const clearViewMiddleware: Middleware = (api) => (next) => async (action) => {
  const actionType = (action as { type: string }).type;
  const initialViewLoad = api.getState().slice.delayedMapBounds === undefined && actionType === 'slice/setDelayedMapViewport';
  if (window.location.pathname.startsWith('/view/') && !initialViewLoad && !ignoreActions.includes(actionType)) {
    // check if the slice has changed,
    const before = sliceSelectors.snapshot(api.getState());
    const result = next(action);
    const after = sliceSelectors.snapshot(api.getState());
    if (!deepEqual(before, after)) {
      window.history.replaceState({}, '', '/');
    }
    return result;
  }

  return next(action);
};

export const store = configureStore({
  reducer: {
    // slices
    slice: sliceReducer,
    filters: filtersReducer,
    statsSlice: statsReducer,
    admin: adminReducer,

    // services
    [service.reducerPath]: service.reducer,
    [api.reducerPath]: api.reducer,
    [staticService.reducerPath]: staticService.reducer,
    [aiService.reducerPath]: aiService.reducer,
    [wifiService.reducerPath]: wifiService.reducer,
    [adminService.reducerPath]: adminService.reducer,
    [ariService.reducerPath]: ariService.reducer,
    [dataminrService.reducerPath]: dataminrService.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      // cont'd services
      service.middleware,
      api.middleware,
      staticService.middleware,
      aiService.middleware,
      wifiService.middleware,
      adminService.middleware,
      ariService.middleware,
      dataminrService.middleware,
      clearViewMiddleware
    ])
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

setupListeners(store.dispatch);
